import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import InfoHeader from "../components/InfoHeader";
import CardDesdobramento from "../components/CardDesdobramento";
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useEffect, useState } from "react";
import SelectAcao from "../components/SelectAcao";
import PlanoAcao from "../types/negocio/PlanoAcao";
import { Desdobramento } from "../types/negocio/Desdobramento";
import { desdobramentoService } from "../services/DesdobramentoService";
import toastService from "../utils/toastService";
import { planoAcaoService } from "../services/PlanoAcaoService";
import { PrimaryButton, SecondaryButton } from "../components";
import SelectColaborador from "../components/SelectColaborador";
import TarefaModal from "../components/modais/TarefaModal";

export default function ThinkBoard(props: Readonly<{ planoAcaoGuid: string }>) {
	const [tasks, setTasks] = useState<{ [key: string]: Desdobramento[] }>({
		pendente: [],
		emAndamento: [],
		finalizada: []
	});
	const [selectedAcao, setSelectedAcao] = useState<PlanoAcao | undefined>();
	const [loadingCards, setLoadingCards] = useState<{ [key: string]: boolean }>({});
	const [responsavel, setResponsavel] = useState('');

	// Modal states
	const [modalAberto, setModalAberto] = useState(false);
	const [modoEdicao, setModoEdicao] = useState(false);
	const [tarefaSelecionada, setTarefaSelecionada] = useState<Desdobramento>({} as Desdobramento);

	// Mapping between column IDs and situacaoKanban values
	const columnToSituacao: Record<string, string> = {
		pendente: 'Pendente',
		emAndamento: 'Em Andamento',
		finalizada: 'Finalizada'
	};

	const criarTarefaVazia = (): Desdobramento => ({
		guid: '',
		planoAcaoGuid: selectedAcao?.guid || "",
		oque: "",
		porQue: "",
		quem: "",
		quemNomeCompleto: "",
		como: "",
		quando: "",
		onde: "",
		quantoCusta: "",
		situacaoKanban: "Pendente",
	});

	useEffect(() => {
		if (props.planoAcaoGuid) {
			planoAcaoService.buscarPorId(props.planoAcaoGuid)
				.then((acao) => {
					if (acao) {
						setSelectedAcao(acao);
					} else {
						toastService.error("Ação não encontrada");
					}
				})
				.catch(error => {
					console.error("Erro ao buscar ação:", error);
					toastService.error("Erro ao carregar ação");
				});
		}
	}, []);

	// Load desdobramentos when an action is selected
	useEffect(() => {
		if (!selectedAcao?.guid) return;

		desdobramentoService.listarPorPlano(selectedAcao.guid, {
			responsavel: responsavel || undefined,
		})
			.then((data) => {
				const updatedTasks: { [key: string]: Desdobramento[] } = {
					pendente: [],
					emAndamento: [],
					finalizada: []
				};
				data.forEach((desdobramento) => {
					if (desdobramento.situacaoKanban === 'Pendente') {
						updatedTasks.pendente.push(desdobramento);
					} else if (desdobramento.situacaoKanban === 'Em Andamento') {
						updatedTasks.emAndamento.push(desdobramento);
					} else if (desdobramento.situacaoKanban === 'Finalizada') {
						updatedTasks.finalizada.push(desdobramento);
					} else {
						// Default to pendente if no situacaoKanban is set
						updatedTasks.pendente.push({
							...desdobramento,
							situacaoKanban: 'Pendente'
						});
					}
				});
				setTasks(updatedTasks);
			})
			.catch(error => {
				console.error("Erro ao carregar tarefas:", error);
				toastService.error("Erro ao carregar tarefas");
			});
	}, [selectedAcao]);

	const buscarDesdobramentos = async () => {
		if (!selectedAcao?.guid) return;

		try {
			const data = await desdobramentoService.listarPorPlano(selectedAcao.guid, {
				responsavel: responsavel || undefined,
			});

			const updatedTasks: { [key: string]: Desdobramento[] } = {
				pendente: [],
				emAndamento: [],
				finalizada: []
			};

			data.forEach((desdobramento) => {
				if (desdobramento.situacaoKanban === 'Pendente') {
					updatedTasks.pendente.push(desdobramento);
				} else if (desdobramento.situacaoKanban === 'Em Andamento') {
					updatedTasks.emAndamento.push(desdobramento);
				} else if (desdobramento.situacaoKanban === 'Finalizada') {
					updatedTasks.finalizada.push(desdobramento);
				} else {
					updatedTasks.pendente.push({ ...desdobramento, situacaoKanban: 'Pendente' });
				}
			});

			setTasks(updatedTasks);
		} catch (error) {
			console.error("Erro ao carregar tarefas:", error);
			toastService.error("Erro ao carregar tarefas");
		}
	};

	const handleEdit = (taskGuid: string) => {
		// Find the task in all columns
		let foundTask: Desdobramento | undefined;
		Object.values(tasks).forEach(columnTasks => {
			const task = columnTasks.find(t => t.guid === taskGuid);
			if (task) foundTask = task;
		});

		if (foundTask) {
			setTarefaSelecionada(foundTask);
			setModoEdicao(true);
			setModalAberto(true);
		}
	};

	const handleViewMore = (taskGuid: string) => {
		// Find the task in all columns
		let foundTask: Desdobramento | undefined;
		Object.values(tasks).forEach(columnTasks => {
			const task = columnTasks.find(t => t.guid === taskGuid);
			if (task) foundTask = task;
		});

		if (foundTask) {
			setTarefaSelecionada(foundTask);
			setModoEdicao(false);
			setModalAberto(true);
		}
	};

	const handleSaveTarefa = async (data: Desdobramento) => {
		try {
			const isNovaTarefa = !data.guid || data.guid.trim() === '';
			
			if (isNovaTarefa) {
				await desdobramentoService.criar(data);
				toastService.success('Tarefa criada com sucesso!');
			} else {
				await desdobramentoService.atualizar(data);
				toastService.success('Tarefa atualizada com sucesso!');
			}
			
			setModalAberto(false);
			
			// Atualizar a lista recarregando as tarefas
			await buscarDesdobramentos();
		} catch (error) {
			console.error('Erro ao salvar tarefa:', error);
			toastService.error('Erro ao salvar tarefa.');
		}
	};

	const onDragEnd = async (result: DropResult) => {
		const { source, destination } = result;
		if (!destination || source.droppableId === destination.droppableId) return;

		// Get the task being moved
		const taskToMove = tasks[source.droppableId as keyof typeof tasks][source.index];

		// Set loading state for this card
		setLoadingCards(prev => ({ ...prev, [taskToMove.guid]: true }));

		// Create new arrays for both source and destination columns
		const sourceTasks = Array.from(tasks[source.droppableId as keyof typeof tasks]);
		const [removed] = sourceTasks.splice(source.index, 1);
		const destinationTasks = Array.from(tasks[destination.droppableId as keyof typeof tasks]);
		destinationTasks.splice(destination.index, 0, removed);

		// Update the task situacaoKanban
		const novaSituacao = columnToSituacao[destination.droppableId];
		const taskToUpdate = { ...removed, situacaoKanban: novaSituacao };

		// Optimistically update UI
		setTasks({
			...tasks,
			[source.droppableId]: sourceTasks,
			[destination.droppableId]: destinationTasks,
		});

		// Update in backend
		try {
			await desdobramentoService.atualizarSituacaoKanban(taskToUpdate.guid, novaSituacao);
			toastService.success(`Situação atualizada para: ${novaSituacao}`);
		} catch (error) {
			console.error("Erro ao atualizar situação do kanban:", error);
			toastService.error("Erro ao atualizar situação do tarefas");

			// Revert the UI change on error
			sourceTasks.splice(source.index, 0, removed);
			destinationTasks.splice(destination.index, 1);

			setTasks({
				...tasks,
				[source.droppableId]: sourceTasks,
				[destination.droppableId]: destinationTasks,
			});
		} finally {
			// Clear loading state
			setLoadingCards(prev => ({ ...prev, [taskToMove.guid]: false }));
		}
	};

	// State for confirmation dialog
	const [deleteConfirmation, setDeleteConfirmation] = useState<{
		isOpen: boolean;
		columnId: string;
		taskId: string;
		taskName: string;
	}>({
		isOpen: false,
		columnId: '',
		taskId: '',
		taskName: ''
	});

	const confirmDelete = (columnId: string, taskId: string, taskName: string) => {
		setDeleteConfirmation({
			isOpen: true,
			columnId,
			taskId,
			taskName
		});
	};

	const handleDeleteConfirmed = async () => {
		const { columnId, taskId } = deleteConfirmation;

		try {
			// Set loading state for this card
			setLoadingCards(prev => ({ ...prev, [taskId]: true }));

			await desdobramentoService.deletar(taskId);

			const updatedTasks = {
				...tasks,
				[columnId]: tasks[columnId as keyof typeof tasks].filter((task) => task.guid !== taskId),
			};
			setTasks(updatedTasks);

			toastService.success("Tarefa excluído com sucesso");
		} catch (error) {
			console.error("Erro ao excluir tarefa:", error);
			toastService.error("Erro ao excluir tarefa");
		} finally {
			// Clear loading state
			setLoadingCards(prev => ({ ...prev, [taskId]: false }));
			// Close the confirmation dialog
			setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
	};

	// Replace the old handleDelete function with this one that shows the confirmation dialog
	const handleDelete = (columnId: string, taskId: string) => {
		const task = tasks[columnId as keyof typeof tasks].find(t => t.guid === taskId);
		if (task) {
			confirmDelete(columnId, taskId, task.oque || 'esta tarefa');
		}
	};

	return (
		<Box>
			<InfoHeader
				title="Think Board"
				message="Analise as tarefas da ação selecionada em formato de quadro Kanban. Arraste os cards para atualizar o status."
				isImportant
				url='https://youtu.be/MHPv-tSLTpU'
				modalTitle='Entenda a aba Tarefa'
			/>

			<Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "flex-end" }}>
				<SelectAcao
					sx={{ flexGrow: 1 }}
					value={selectedAcao ?? null}
					onChange={(newValue) => setSelectedAcao(newValue)}
				/>

				{/* Responsável */}
				<Box sx={{ flexGrow: 1 }}>
					<SelectColaborador
						value={responsavel}
						onChange={(colaborador) => setResponsavel(colaborador?.guid ?? '')}
					/>
				</Box>

				{/* Botão Filtrar */}
				<PrimaryButton
					variant="contained"
					color="primary"
					onClick={buscarDesdobramentos}
					sx={{ minWidth: "120px", height: "56px" }}
				>
					Filtrar
				</PrimaryButton>
			</Box>

			<DragDropContext onDragEnd={onDragEnd}>
				<Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
					{[
						{ id: 'pendente', title: 'Pendentes' },
						{ id: 'emAndamento', title: 'Em Andamento' },
						{ id: 'finalizada', title: 'Finalizadas' },
					].map((column) => (
						<Box
							key={column.id}
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								width: '32%',
								backgroundColor: 'white',
								borderRadius: '8px',
								height: '62vh',
							}}
						>
							<Box
								sx={{
									width: '100%',
									backgroundColor: (theme) => theme.palette.primary.main,
									textAlign: 'center',
									py: 1,
									borderStartEndRadius: '8px',
									borderStartStartRadius: '8px',
								}}
							>
								<Typography sx={{ color: 'white' }}>{column.title}</Typography>
							</Box>

							<Droppable droppableId={column.id}>
								{(provided, snapshot) => (
									<Box
										ref={provided.innerRef}
										{...provided.droppableProps}
										sx={{
											flexGrow: 1,
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
											px: 2,
											overflowY: 'auto',
											borderRadius: '0 0 8px 8px',
											border: snapshot.isDraggingOver ? '2px dashed #1976d2' : 'none',
											transition: 'border 0.2s ease',
										}}
									>
										{tasks[column.id as keyof typeof tasks].map((task, index) => (
											<Draggable key={task.guid} draggableId={task.guid} index={index}>
												{(provided, snapshot) => (
													<Box
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														sx={{
															cursor: 'grab',
															opacity: snapshot.isDragging ? 0.7 : 1,
															transition: 'opacity 0.2s ease',
														}}
													>
														<CardDesdobramento
															desdobramento={task}
															onEdit={() => handleEdit(task.guid)}
															onDelete={() => handleDelete(column.id, task.guid)}
															onViewMore={() => handleViewMore(task.guid)}
															isLoading={loadingCards[task.guid] || false}
														/>
													</Box>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</Box>
								)}
							</Droppable>
						</Box>
					))}
				</Box>
			</DragDropContext>

			<TarefaModal
				open={modalAberto}
				onClose={() => setModalAberto(false)}
				desdobramento={tarefaSelecionada}
				modoEdicao={modoEdicao}
				setDesdobramento={modoEdicao ? setTarefaSelecionada : undefined}
				onSave={modoEdicao ? handleSaveTarefa : undefined}
				onEditClick={() => setModoEdicao(true)}
				isNovaTarefa={!tarefaSelecionada?.guid || tarefaSelecionada.guid.trim() === ''}
			/>

			{/* Confirmation Dialog */}
			<Dialog
				open={deleteConfirmation.isOpen}
				onClose={handleCancelDelete}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					Confirmar exclusão
				</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja excluir a tarefa "{deleteConfirmation.taskName}"?
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						Esta ação não pode ser desfeita.
					</Typography>
				</DialogContent>
				<DialogActions>
					<SecondaryButton onClick={handleCancelDelete} variant="contained">
						Cancelar
					</SecondaryButton>
					<PrimaryButton
						onClick={handleDeleteConfirmed}
						color="error"
						variant="contained"
						autoFocus
					>
						Excluir
					</PrimaryButton>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
